/**
 * @fileoverview TipTap Page Break Node Extension
 *
 * Purpose: Adds a manual page break the user can insert anywhere in the
 * document. The browser PDF paginator and the Word export both honour this
 * marker and split the page where it appears.
 *
 * Serialised shape:
 *   <div data-page-break class="editor-page-break"></div>
 *
 * Why a `<div>` and not `<hr>` or a custom element:
 *   - Round-trips through DOMPurify, the `.prose-letter` preview, and the
 *     Word altChunk pipeline without configuration changes (`<div>` is on
 *     every sanitiser allow-list; the `data-` attribute drives behaviour).
 *   - Easy CSS hook for the in-editor visual placeholder (dashed line
 *     labelled "Page break") and the print-time `display: none`.
 *
 * The node is treated as a block-level atom so deleting it removes the
 * whole element with one Backspace, just like Word's manual page break.
 */

import { Node, mergeAttributes } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    pageBreak: {
      /** Insert a manual page break at the current cursor position. */
      insertPageBreak: () => ReturnType;
    };
  }
}

export const PageBreak = Node.create({
  name: "pageBreak",
  group: "block",
  /** Atom — selects/deletes as a single unit. */
  atom: true,
  selectable: true,
  /**
   * Draggable — lets the user grab the marker and reposition it within the
   * document, the same way images can be dragged. ProseMirror only treats a
   * leaf/atom node as user-draggable when its spec opts in here; without it
   * the marker is selectable but inert on drag. The Dropcursor/Gapcursor
   * extensions bundled in StarterKit handle the drop target and the move.
   */
  draggable: true,
  /** Block-level (occupies a line of its own). */
  isolating: true,

  parseHTML() {
    return [
      {
        tag: "div[data-page-break]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-page-break": "",
        class: "editor-page-break",
      }),
    ];
  },

  addCommands() {
    return {
      insertPageBreak:
        () =>
        ({ commands, state }) => {
          // A naive `insertContent({ type: "pageBreak" })` runs at the
          // cursor position and asks the schema "can I put a block atom
          // here?". The answer is "no" inside table cells, list items,
          // headings, and any other non-paragraph context — so the
          // command silently no-ops, which is exactly the "sometimes it
          // won't insert" bug users hit.
          //
          // Instead we hoist the insertion to the top-level position
          // right AFTER whichever top-level block currently contains
          // the cursor. This always lands on a doc-level boundary
          // where pageBreak is unambiguously legal, and matches Word's
          // "insert here" behaviour for pages inside tables/lists
          // (the break appears after the surrounding block).
          const { selection, doc } = state;
          const $from = selection.$from;

          // `after(1)` returns the position immediately following the
          // top-level block (depth 1) that wraps the cursor — works
          // identically whether the cursor is inside a paragraph,
          // heading, list item, table cell, or nested combination.
          // Cursor is at the doc root with no surrounding block —
          // append to the end as a fallback.
          const insertPos: number =
            $from.depth >= 1 ? $from.after(1) : doc.content.size;

          return commands.insertContentAt(insertPos, [
            { type: this.name },
            { type: "paragraph" },
          ]);
        },
    };
  },
});
